import { useCallback } from "react";
import { useParams } from "react-router";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import adminApi from "../../../../api/adminApi";
import type { ContactDetails } from "../../../../api/types/fundStructureTypes";
import useClientContext from "../../../../context/useClientContext";
import ContactDetailsPage from "./ContactDetailsPage";

const FundStructureContactPage = () => {
  usePageTitle("Contact");

  const { accessCategories } = useClientContext();

  const { id } = useParams();

  const getContactDetails = useCallback(() => adminApi.getContactDetails(defined(id)), [id]);
  const [contactDetails, fetchContactDetailsError, { setData: setContactDetails, fetch: fetchContactDetails }] =
    useFetch(getContactDetails);

  const getObjectDefinition = useCallback(() => adminApi.getObjectDefinition("Contact"), []);
  const [objectDefinitionWithPermissions, fetchDefinitionError] = useFetch(getObjectDefinition);

  const handleContactDetailsChange = useCallback(
    (details: Partial<ContactDetails>) => {
      setContactDetails((prev) => (prev ? { ...prev, ...details } : undefined));
    },
    [setContactDetails]
  );

  if (fetchContactDetailsError || fetchDefinitionError) {
    logError(fetchContactDetailsError || fetchDefinitionError, "[FundStructureContactPage]");
    return <DataLoadingFailed title="Loading contact details failed" />;
  }

  if (!contactDetails || !objectDefinitionWithPermissions) {
    return <InlineLoader />;
  }

  return (
    <ContactDetailsPage
      key={contactDetails.contact.id}
      categories={accessCategories}
      contactDetails={contactDetails}
      objectDefinition={objectDefinitionWithPermissions.objectClassDefinition}
      objectPermissions={objectDefinitionWithPermissions.objectPermissions}
      onContactDetailsChange={handleContactDetailsChange}
      onUpdateContactDetails={fetchContactDetails}
    />
  );
};

export default FundStructureContactPage;
